import React from 'react';
import getConfig from 'next/config';
import { Row, Col, Divider } from 'antd';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import * as gtag from 'lib/gtag';
import { GetServerSideProps } from 'next';
import BasicLayout from 'layout/Basic';
import { convertProps } from 'lib/parseAPIResponse';
import GalleryCard from 'components/GalleryCard';
import VideosCarousel from 'components/VideoesCarousel ';
import Tags from 'components/Tags';
import { HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import FormattedDate from 'components/FormattedDate';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import Section, { SectionTitle } from 'components/Section';
import Newsletter from 'containers/Newsletter';
import Subscription from 'containers/Subscription';
import CompanyCarousel from 'components/CompanyCarousel';
import ShareButtons from 'components/ShareButtons';
import { merriweather } from 'styles/GlobalStyle';
import VideoPlayer from 'react-player/youtube';

const Description = styled.div`
    font-size: 23px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.52;
    letter-spacing: normal;
    text-align: left;
    padding: 20px;
    @media (max-height: 992px) {
        padding: 25px 0 0 0;
    }
    overflow: hidden;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const Date = styled.div`
    color: ${(props) => props.color || props.theme.colors.greyishBrown};
    font-weight: 300;
    font-family: ${merriweather.style.fontFamily};
    line-height: 18px;
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.41;
    letter-spacing: normal;
    text-align: left;
    color: #707070;
`;

interface Props {
    video: Video;
    videos: Video[];
    article: Article;
    contentRows: string[];
    events: Occurrence[];
    eventsPatronate: Company[];
    latestIssue: Issue;
    seeAlsoArticles: Article[];
    singleGallery: Gallery;
    singleVideo: Video;
    topOneInCampaign: {
        articles: Article[];
        campaign: Campaign[];
    };
    topInCategory: Article[];
    whitePapers: WhitePaper[];
    image: number;
    category: Category[];
    ads: PageAds;
}

export const VideoDetailsPage = ({
    video,
    videos,
    singleGallery,
    latestIssue,
    ads
}: Props): React.JSX.Element => {
    const { url } = getConfig().publicRuntimeConfig;
    const router = useRouter();
    const handleStart = () => {
        gtag.event({
            action: 'Play',
            category: 'Videos',
            label: video.title,
            value: router.asPath
        });
    };
    const companies = video.companies.filter((company: Company): boolean => company.premium);
    const description = video.seoDescription || video.description?.replace(/<[^>]*>?/gm, '');

    const { rectangle, skyscraper, wideboardBottom, halfpage } = ads;
    return (
        <BasicLayout ads={ads}>
            <NextSeo
                title={video.seoTitle || video.title}
                canonical={`${url}/wideo/${video.seoSlug}`}
                description={description}
                openGraph={{
                    title: video.title,
                    description: description,
                    url: `${url}/wideo/${video.seoSlug}`,
                    type: 'video.other',
                    video: {
                        releaseDate: video.dateDisplayed,
                        tags: video.tags.map((tag) => tag.name)
                    }
                }}
            />
            <Row gutter={[16, 32]}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="Wideo" url="/wideo" noArrow />
                            <Title>{video.title}</Title>
                        </Col>
                    </Row>
                    <Row style={{ overflow: 'hidden' }} justify="center">
                        <Col span={24}>
                            <VideoPlayer
                                url={video.videoUrl}
                                // light={video.openingImage}
                                onStart={handleStart}
                            />
                        </Col>
                    </Row>
                    <Section>
                        <Col span={24}>
                            <Row>
                                <Col xs={24} md={12}>
                                    <SkyscraperMobile id={skyscraper[0]} margin="0 auto" />
                                </Col>
                                <Col xs={24} md={12}>
                                    <SkyscraperMobile
                                        id={skyscraper[1]}
                                        margin="35px auto 35px auto"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} md={12}>
                                    <Date>
                                        <FormattedDate date={video.dateDisplayed} />
                                    </Date>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Row justify="end">
                                        <Col>
                                            <ShareButtons
                                                title={video.seoTitle || video.title}
                                                teaser={video.seoDescription || video.description}
                                                url={`${url}/wideo/${video.seoSlug}`}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24} md={20}>
                            <Description dangerouslySetInnerHTML={{ __html: video.description }} />
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    <Section gutter={[16, 16]}>
                        <Col span={24}>
                            <Divider />
                        </Col>
                    </Section>
                    {video.tags && video.tags.length > 0 && (
                        <Section gutter={[16, 16]}>
                            <Col span={24}>
                                <SectionTitle title="Powiązane tagi" />
                            </Col>
                            <Col span={24}>
                                <Tags tags={video.tags} />
                            </Col>
                            <Col span={24}>
                                <Divider />
                            </Col>
                        </Section>
                    )}
                    <Section>
                        <Col span={24}>
                            <HalfPage id={halfpage[0]} />
                        </Col>
                    </Section>
                </Sider>
            </Row>
            {companies && companies.length > 0 && (
                <Row>
                    <Col span={24}>
                        <Row>
                            <Col span={24}>
                                <SectionTitle title="Powiązane firmy" />
                            </Col>
                            <Col span={24}>
                                <CompanyCarousel companies={companies} />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}
            <Row>
                <Col span={24}>
                    <Wideboard id={wideboardBottom[1]} />
                </Col>
            </Row>
            <Section gutter={24}>
                <Col xs={24} md={18}>
                    <SectionTitle title="Zobacz inne wideo" url="/wideo" />
                    <VideosCarousel videos={videos} itemsPerRow={3} />
                </Col>
                <Col xs={24} md={6}>
                    <SectionTitle title="Galeria" url="/galerie" />
                    <GalleryCard
                        images={singleGallery.images}
                        slug={singleGallery.seoSlug}
                        category={singleGallery.category}
                        title={singleGallery.title}
                    />
                </Col>
                <Col span={24}>
                    <Row gutter={16}>
                        <Content>
                            <Section gutter={[16, 32]} justify="center">
                                <Col span={24}>
                                    <Divider />
                                </Col>
                                <Col xs={24} md={20}>
                                    <Newsletter />
                                </Col>
                                <Col span={24}>
                                    <Divider />
                                </Col>
                            </Section>
                        </Content>
                        <Sider>
                            <Row>
                                <Col span={24}>
                                    <Rectangle id={rectangle[0]} />
                                </Col>
                            </Row>
                        </Sider>
                    </Row>
                </Col>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
                <Col span={24}>
                    <Section>
                        <Col span={24}>
                            <Wideboard id={wideboardBottom[0]} />
                        </Col>
                    </Section>
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    const { apiUrl } = getConfig().publicRuntimeConfig;
    return Promise.all([
        fetch(`${apiUrl}/public/api/videos/${params.slug}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=videos/${params.slug}`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default VideoDetailsPage;
